import { isBrowser } from "@/utils/env";

export const getSessionTypeFromUrl = () => {
  const urlParams = new URLSearchParams(isBrowser() ? window.location.search : {});
  return urlParams.get("session_type") || "";
};

/** Use for formatting anchor link for gatsby`s navigate() fns and <Link> component.
 *  Gatsby can`t redirect to anchor, and it causes troubles like your/url/#anchor
 * That fn was written for formatting such links to your/url#anchor
 * _ */
export const formatSeoSafeAnchorLink = (link: string, pathname: string) =>
  (link.startsWith("#") ? pathname + link : link);

/**
 * Utility function to retrieve a URL parameter by its key.
 * Extracts the specified query parameter from the current page's URL and returns its value.
 * Useful for cases where dynamic data in the URL needs to be accessed in the code.
 * @param {string} key - The name of the query parameter to retrieve.
 * @returns {string|null} - The value of the query parameter or null if not found.
 */

export const getUrlParameterByKey = (key: string) => {
  const urlParams = new URLSearchParams(isBrowser() ? window.location.search : {});
  return urlParams.get(key);
};

/**
 * Extracts the successUrl query parameter from the provided string.
 * Accepts either a full URL (e.g., "https://site.com/path?successUrl=/foo")
 * or a raw query string (e.g., "?successUrl=/foo").
 * No direct access to browser APIs is used, making it easy to unit-test.
 * @param {string} searchOrUrl - Full URL or query string to parse.
 * @returns {string|null} The value of the successUrl parameter or null if absent.
 */
export const getSuccessUrl = (searchOrUrl: string) => {
  const search = searchOrUrl.includes("?")
    ? searchOrUrl.split("?")[1] || ""
    : searchOrUrl.replace("?", "");
  const urlParams = new URLSearchParams(search);
  const successUrl = urlParams.get("successUrl");

  return successUrl || null;
};

/**
 * Returns the Avito-specific fixed payment amount from URL search string,
 * or null if not present or invalid.
 * Ignored when `product-code` is also present (product path takes precedence).
 * @param {string} searchOrUrl - Full URL or query string to parse.
 * @returns {number|null} Parsed positive integer amount or null.
 */
export const readAvitoFixedAmount = (searchOrUrl: string): number | null => {
  const search = searchOrUrl.includes("?")
    ? searchOrUrl.split("?")[1] || ""
    : searchOrUrl.replace("?", "");
  const urlParams = new URLSearchParams(search);
  // Product-code fixed payment takes precedence — leave it untouched
  if (urlParams.get("product-code")) {
    return null;
  }
  const raw = urlParams.get("amount");
  if (!raw) return null;
  const n = parseInt(raw, 10);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
};

/**
 * Utility function to check if the current URL path contains a specific string.
 * Useful for determining page type or context based on URL segments.
 * @param {string} pathSegment - The string to search for in the current path.
 * @param {string} [pathname] - Path to check instead of `window.location` (e.g. from `useLocation`, works in SSR).
 * @returns {boolean} - True if the path contains the specified segment, false otherwise.
 */
export const isPathContains = (pathSegment: string, pathname?: string): boolean => {
  const pathSource = pathname ?? (isBrowser() ? window.location.pathname : "");
  if (!pathSource) return false;
  const currentPath = pathSource.replace(/\/$/, "");
  return currentPath.includes(pathSegment);
};

/**
 * Checks whether current path starts with a URL segment.
 * Example: "/quiz/relationships-crisis" starts with "/quiz".
 * @param {string} pathPrefix - Prefix to match (with or without leading slash).
 * @returns {boolean} - True if current path starts with prefix.
 */
const normalizePath = (value: string) => {
  const withLeadingSlash = value.startsWith("/") ? value : `/${value}`;
  const trimmed = withLeadingSlash.replace(/\/$/, "");
  return trimmed || "/";
};

export const isPathStartsWith = (pathPrefix: string, pathname?: string): boolean => {
  const pathSource = pathname ?? (isBrowser() ? window.location.pathname : "");
  if (!pathSource) return false;

  const currentPath = normalizePath(pathSource);
  const normalizedPrefix = normalizePath(pathPrefix);

  return currentPath === normalizedPrefix
    || currentPath.startsWith(`${normalizedPrefix}/`);
};

/**
 * Pages that use landing layout without mobile bottom navigation.
 * Cookie banner and other fixed bottom UI should not reserve menu offset on these routes.
 */
export const isQuizPage = (pathname?: string): boolean =>
  isPathStartsWith("/quiz", pathname);

export const isPageWithoutMobileBottomMenu = (pathname?: string): boolean =>
  isPathStartsWith("/landing", pathname) || isQuizPage(pathname);

export const PAGE_LAYOUT_MINIMAL = "minimal";
export const PAGE_LAYOUT_QUIZ = "quiz";
export const PAGE_LAYOUT_DEFAULT = "default";

export const getPageLayoutType = (pathname?: string) => {
  if (isQuizPage(pathname)) {
    return PAGE_LAYOUT_QUIZ;
  }

  return isPageWithoutMobileBottomMenu(pathname) ? PAGE_LAYOUT_MINIMAL : PAGE_LAYOUT_DEFAULT;
};

/** Syncs layout hint for CSS before React hydration to avoid header/menu flash. */
export const syncPageLayoutBodyAttribute = (pathname?: string): void => {
  if (!isBrowser()) {
    return;
  }

  const pathSource = pathname ?? window.location.pathname;
  document.body.dataset.pageLayout = getPageLayoutType(pathSource);
};

/**
 * Utility function to check if a link is external (starts with http:// or https://).
 * Used to determine whether to use Gatsby Link component or regular <a> tag.
 * @param {string} link - The URL to check.
 * @returns {boolean} - True if the link is external, false otherwise.
 */
export const isExternalLink = (link: string): boolean =>
  link.startsWith("http://") || link.startsWith("https://");

export const reloadPage = (): void => {
  if (isBrowser()) {
    window.location.reload();
  }
};
